/**
 * Payment terms for the quotation — `lookups.get_payment_terms`, and the picker over it.
 *
 * Live shape 2026-09-14: `{type: "payment_terms", data: [{name, payment_terms_name, description,
 * due_date_based_on, payment_days}]}` — 40-odd templates ("10 Days PDC", "100 % advance along
 * with order", …). The quotation stores the chosen one in `payment_terms_template` (persists
 * through `update_quotation`, probed on QTN-2026-00017).
 *
 * The list is small and static, so it is fetched once and searched client-side; the picker's
 * `useOptions` contract (`LinkCombo`) is met by filtering the cached list.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { callGet } from './bosun'
import { num, str } from './wire'
import type { PickerOption } from './useEnquiryFromMail'

export interface PaymentTerm {
  name: string
  description: string
  due_date_based_on: string
  payment_days: number
}

export function toPaymentTerms(wire: unknown): PaymentTerm[] {
  const rows = (wire as { data?: unknown } | null)?.data
  if (!Array.isArray(rows)) return []
  return (rows as Record<string, unknown>[])
    .map((r) => ({
      name: str(r.name) || str(r.payment_terms_name),
      description: str(r.description),
      due_date_based_on: str(r.due_date_based_on),
      payment_days: num(r.payment_days),
    }))
    .filter((t) => t.name !== '')
}

/** One line under the picker saying what the term means — "30 day(s) after invoice date". */
export function describePaymentTerm(term: PaymentTerm): string {
  if (term.payment_days > 0 && term.due_date_based_on !== '') {
    return `${term.payment_days} ${term.due_date_based_on.replace(/^Day\(s\)\s*/i, 'day(s) ')}`.replace(/\s+/g, ' ').trim()
  }
  return term.description
}

export function usePaymentTerms() {
  return useQuery({
    queryKey: ['lookups', 'payment-terms'] as const,
    queryFn: async () => toPaymentTerms(await callGet<unknown>('lookups', 'get_payment_terms')),
    staleTime: 30 * 60_000,
  })
}

/** `LinkCombo`'s `useOptions` shape, over the cached list. */
export function usePaymentTermsPicker(search: string): { data?: PickerOption[]; isPending: boolean } {
  const q = usePaymentTerms()
  const needle = search.trim().toLowerCase()
  const data = useMemo(
    () =>
      q.data
        ?.filter((t) => needle === '' || t.name.toLowerCase().includes(needle))
        .map((t) => ({ name: t.name, label: t.name, sublabel: describePaymentTerm(t) || undefined })),
    [q.data, needle],
  )
  return { data, isPending: q.isPending }
}
